/**
 * All user-facing copy for the Add Interview Activity wizard.
 * Centralised here so PM can review and update text without digging through components.
 *
 * Usage:
 *   import { INTERVIEW_ACTIVITY_COPY } from './copy';
 *   INTERVIEW_ACTIVITY_COPY.toast.loadError.title
 */

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------

const wizard = {
  steps: {
    details: { label: 'Details', value: 'details' },
    communication: { label: 'Communication', value: 'communication' },
    scorecard: { label: 'Scorecard', value: 'scorecard' },
  },
  saveLabel: 'Save interview',
  buttons: {
    cancel: 'Cancel',
    close: 'Close',
  },
  emptyState: {
    loadFailed: 'This activity could not be loaded.',
  },
} as const;

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

const toast = {
  loadError: {
    title: 'Could not load this activity',
    description: 'Try closing and opening the editor again.',
  },
  missingJobOrStage: {
    title: 'Missing job or stage',
    description: 'Refresh the page and try again.',
  },
  createSuccess: {
    title: 'Interview activity created',
  },
  updateSuccess: {
    title: 'Interview activity updated',
  },
  saveError: {
    title: 'Could not save interview activity',
    serverError: 'Something went wrong on the server.',
    fallback: 'Please try again.',
  },
} as const;

// ---------------------------------------------------------------------------
// API error codes → user-facing copy
// ---------------------------------------------------------------------------

const apiErrors: Record<string, string> = {
  err_invalid_syntax:
    'Invalid variables found in a calendar invite or email. Update or remove them to save.',
  err_invalid_options:
    'Check Details: add at least one interviewer when using manual selection, or fix scheduling/email options.',
};

// ---------------------------------------------------------------------------
// Details step
// ---------------------------------------------------------------------------

const details = {
  sectionLabel: 'Interview details',
  scheduleSectionLabel: 'Schedule details',
  duration: {
    label: 'Duration',
    unit: 'mins',
  },
  calendarVisibility: {
    label: 'Make the interview private',
    subLabel: 'Hide calendar event details from non-attendees',
  },
  host: {
    label: 'Host',
    tooltip:
      'Pick who hosts the Zoom call. "Organizer" uses the recruiter who ' +
      'schedules the interview; "One of the interviewers" uses an ' +
      'interviewer attending the interview. The chosen user must be part ' +
      "of your organization's Zoom account.",
    options: {
      organizer: 'Organizer',
      interviewer: 'One of the interviewers',
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Communication step
// ---------------------------------------------------------------------------

const communication = {
  tabs: {
    candidate: 'Candidate communication',
    interviewer: 'Interviewer communication',
    selfSchedule: 'Self-schedule slot selection email',
  },
  organizerSender: {
    label: "Organizer's email",
    description:
      'Whoever is scheduling the interview will be prefilled as the sender',
  },
  candidate: {
    calendarTitle: "Candidate's calendar invite",
    emailTitle: "Candidate's email",
  },
  interviewer: {
    calendarTitle: "Interviewer's calendar invite",
    emailTitle: "Interviewer's email",
  },
  selfSchedule: {
    title: 'Self-schedule slot selection email',
    description:
      'Candidates get this email to select their interview slot for self-schedule interviews',
  },
  placeholders: {
    template: 'Select a template',
    from: 'Select from',
    subject: 'Additional email for context',
    selfScheduleSubject: 'Subject',
  },
  icsCheckbox: 'Include calendar invite as an attachment (.ics file)',
} as const;

// ---------------------------------------------------------------------------
// Validation messages
// ---------------------------------------------------------------------------

const SUBJECT_MAX_LENGTH = 150;

const validation = {
  subjectMaxLength: SUBJECT_MAX_LENGTH,
  subjectRequired: 'Subject is required',
  subjectTooLong: `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`,
  fromRequired: 'From is required',
  bodyRequired: 'Body is required',
  // Canonical wording for any malformed/unknown template variable (lone
  // `{{` delimiters AND unknown variable names). Shared across the
  // InterviewActivity comm validator and the ScheduleInterview slot-
  // selection-email validator so editor onChange and Save onClick
  // surface identical copy.
  subjectInvalidVariable:
    'Subject contains an invalid variable. Use the {{variable_name}} format.',
  bodyInvalidVariable:
    'Body contains an invalid variable. Use the {{variable_name}} format.',
} as const;

// ---------------------------------------------------------------------------
// Scorecard step
// ---------------------------------------------------------------------------

const scorecard = {
  label: 'Scorecard',
  enableLabel: 'Enable scorecards for this interview',
  aiLabel: 'Allow interviewers to complete scorecards with AI?',
  aiDescription:
    'Helps draft scorecards faster, improving completion rates and consistency',
  templatePlaceholder: 'Select template',
  templateNote:
    'Note: This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
} as const;

// ---------------------------------------------------------------------------
// Single export
// ---------------------------------------------------------------------------

export const INTERVIEW_ACTIVITY_COPY = {
  wizard,
  toast,
  apiErrors,
  details,
  communication,
  validation,
  scorecard,
} as const;
